import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { injectStepUpTokenIntoAxiosClientHeader } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";
import { StepUpRefreshableState, useWfAuthn, useWfAuthnIdle } from "auth-sdk";

import { ErrorComponent } from "../../error-handling/error-component";
import { FullScreenLoader } from "../loader/loader";

import { IdlenessTracker } from "./idleness-tracker";

const MissingMFAErrorComponent = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig({ refetchOnMount: false });

  return (
    <ErrorComponent
      {...{
        title: intl.formatMessage({
          id: "shared-ui: no mfa present for account title",
          defaultMessage: "No MFA Present for Account",
        }),
        message: intl.formatMessage({
          id: "shared-ui: no mfa present for account message",
          defaultMessage:
            "To access banking your account must have an enrolled multi-factor authentication (MFA) method. Please enroll an MFA method to continue.",
        }),
        primaryAction: {
          content: "MFA Settings",
          onAction: () => {
            window.location.href = `/${customerDomain}/settings?tab=user`;
          },
        },
        secondaryAction: {
          content: "Go back",
          onAction: () => {
            window.history.back();
          },
        },
      }}
    />
  );
};

const MFARedirectionComponent = ({ message }: { message: string }) => {
  const authn = useWfAuthn();
  const intl = useIntl();

  return (
    <ErrorComponent
      {...{
        title: intl.formatMessage({
          id: "shared-ui: mfa expired title",
          defaultMessage: "Authentication Expired",
        }),
        message: message,
        primaryAction: {
          content: intl.formatMessage({
            id: "shared-ui: reauthenticate button",
            defaultMessage: "Reauthenticate",
          }),
          onAction: () => {
            authn.stepUp({
              next: window.location.pathname,
            });
          },
        },
      }}
    />
  );
};

export const ConditionalStepUpMfaGuardWrapper = ({ condition, children }) =>
  condition ? <StepUpMfaGuard>{children}</StepUpMfaGuard> : children;

export const StepUpMfaGuard = ({ children }: { children: React.ReactNode }) => {
  const authn = useWfAuthn();
  const intl = useIntl();
  const [hasMfa, setHasMfa] = useState<boolean | undefined>(undefined);
  const { state } = useWfAuthnIdle();
  const [interceptorReady, setInterceptorReady] = useState(false);
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    const updateToken = async () => {
      let hasMfaEnrolled = await authn.hasMfaEnrolled();
      let currentStepUpToken;

      if (hasMfaEnrolled === undefined) {
        // Force us to refresh the ID token if it's undefined. We've observed this happens about half an hour
        // after stepping up.
        hasMfaEnrolled = await authn.hasMfaEnrolled({ refresh: true });
      }
      setHasMfa(hasMfaEnrolled);
      if (!hasMfaEnrolled) {
        return;
      }

      // Do nothing if a fetch is currently in progress. Ommitting this check can cause an infinite loop as
      // getStepUpToken mutates this state
      if (authn.isFetching) {
        return;
      }

      if (!interceptorReady && state === StepUpRefreshableState.EXPIRED) {
        // this will redirect the user away from the app to do MFA step-up
        // we should only do this redirection automatically before we have a valid token (i.e. the first time
        // the user visits a page under the step-up guard) which is why we check for interceptorReady. Future
        // expirations should be handled by the MFARedirectionComponent
        await authn.stepUp({ next: window.location.pathname });
      } else if (state === StepUpRefreshableState.NEARING_IDLE) {
        // token is nearing idle so force a refresh to keep it active
        currentStepUpToken = await authn.getStepUpToken({ refresh: true });
      } else if (state === StepUpRefreshableState.VALID || state === StepUpRefreshableState.NEARING_EXPIRED) {
        // token is valid so inject it
        currentStepUpToken = await authn.getStepUpToken();
      } // else state is FETCHING so wait for it to reach a steady-state

      if (currentStepUpToken) {
        // token is valid so inject it into the axios client header
        injectStepUpTokenIntoAxiosClientHeader(currentStepUpToken);
        setInterceptorReady(true);
      }
    };
    updateToken();
  }, [state, authn, hasMfa, interceptorReady]);

  if (hasMfa === false) {
    return <MissingMFAErrorComponent />;
  } else if (!isIdle && (hasMfa === undefined || !interceptorReady)) {
    // Show a loader when:
    // - we're waiting to retrieve whether or not the user is MFA enrolled
    // - we're waiting to inject a step-up token into the axios client header
    // Only do this when we're not in the idle state as otherwise the switch to the loader will dismiss the current
    // idle modal and reset the timeout
    return <FullScreenLoader />;
  } else if (state === StepUpRefreshableState.EXPIRED) {
    return (
      <MFARedirectionComponent
        message={intl.formatMessage({
          id: "shared-ui: mfa expired message",
          defaultMessage: "Your authentication has expired. Please reauthenticate to continue.",
        })}
      />
    );
  } else if (isIdle) {
    return (
      <MFARedirectionComponent
        message={intl.formatMessage({
          id: "shared-ui: mfa idle message",
          defaultMessage: "Your authentication has expired due to inactivity. Please reauthenticate to continue.",
        })}
      />
    );
  } else {
    return <IdlenessTracker onIdle={() => setIsIdle(true)}>{children}</IdlenessTracker>;
  }
};
